import { useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ExternalLink, Github, Store, X } from 'lucide-react';
import { projectCategories, projects } from '../data/content';
import type { Project, ProjectCategory } from '../types/portfolio';
import { SectionHeading } from './SectionHeading';

function statusLabel(status: Project['status']) {
  if (status === 'completed') return 'Completed';
  if (status === 'available') return 'Available';
  return 'Important';
}

function ProjectLinks({ project }: { project: Project }) {
  return (
    <div className="project-links">
      {project.github ? (
        <a href={project.github} target="_blank" rel="noreferrer">
          <Github size={17} />
          GitHub
        </a>
      ) : null}
      {project.steam ? (
        <a href={project.steam} target="_blank" rel="noreferrer">
          <Store size={17} />
          Steam
        </a>
      ) : null}
      <a href={project.demo} target="_blank" rel="noreferrer">
        <ExternalLink size={17} />
        Demo
      </a>
    </div>
  );
}

export function Projects() {
  const [activeCategory, setActiveCategory] = useState<ProjectCategory>('All');
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);

  const filteredProjects = useMemo(() => {
    if (activeCategory === 'All') return projects;
    return projects.filter((project) => project.category === activeCategory);
  }, [activeCategory]);

  return (
    <section className="section-band" id="projects">
      <div className="container">
        <SectionHeading
          eyebrow="Projects"
          title="My works"
          text="I've completed your projects: Asset Store, Bot Launcher, Steam Curator and All Web-Site."
        />

        <div className="filter-bar reveal" role="tablist" aria-label="Project categories">
          {projectCategories.map((category) => (
            <button
              className={activeCategory === category ? 'is-active' : ''}
              key={category}
              type="button"
              onClick={() => setActiveCategory(category)}
            >
              {category}
            </button>
          ))}
        </div>

        <div className="projects-grid">
          <AnimatePresence mode="popLayout">
            {filteredProjects.map((project) => (
              <motion.article
                layout
                className="project-card reveal"
                key={project.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 20 }}
                whileHover={{ y: -8, rotateX: 2, rotateY: -2 }}
              >
                <button className="project-preview-button" type="button" onClick={() => setSelectedProject(project)}>
                  <img src={project.image} alt={`${project.title} preview`} loading="lazy" />
                  <span className={`project-status status-${project.status}`}>{statusLabel(project.status)}</span>
                </button>
                <div className="project-body">
                  <span className="project-category">{project.category}</span>
                  <h3>{project.title}</h3>
                  <p>{project.description}</p>
                  <div className="tech-list">
                    {project.tech.map((tech) => (
                      <span key={tech}>{tech}</span>
                    ))}
                  </div>
                  <ProjectLinks project={project} />
                </div>
              </motion.article>
            ))}
          </AnimatePresence>
        </div>
      </div>

      <AnimatePresence>
        {selectedProject ? (
          <motion.div
            className="modal-backdrop"
            role="dialog"
            aria-modal="true"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelectedProject(null)}
          >
            <motion.div
              className="project-modal"
              initial={{ opacity: 0, scale: 0.94, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.94, y: 20 }}
              onClick={(event) => event.stopPropagation()}
            >
              <button className="modal-close" type="button" aria-label="Close preview" onClick={() => setSelectedProject(null)}>
                <X size={20} />
              </button>
              <img src={selectedProject.image} alt={`${selectedProject.title} full preview`} />
              <div>
                <span className="project-category">{selectedProject.category}</span>
                <h3>{selectedProject.title}</h3>
                <p>{selectedProject.description}</p>
                <div className="tech-list">
                  {selectedProject.tech.map((tech) => (
                    <span key={tech}>{tech}</span>
                  ))}
                </div>
                <ProjectLinks project={selectedProject} />
              </div>
            </motion.div>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </section>
  );
}
